import React from 'react';
import { useId } from '@reach/auto-id';
import {
  DependentParams,
  ParamType,
  PortfolioReportParamDTO,
  usePortfolioReportCache,
} from '../../api/usePortfolioReportParamsQuery';
import { Wrapper } from '../../../CommonComponents/OPL/Wrapper/Wrapper';
import { SingleDatePicker } from '../../../CommonComponents/OPL/dates/SingleDatePicker/SingleDatePicker';
import { dateToString, stringToDate } from '../../utils/dates';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';
import { Label } from '../../../CommonComponents/OPL/Label/Label';
import { EntityTypeSelect } from '../EntityTypeSelect/EntityTypeSelect';
import { DateRangePickerWithPresets } from '../DateRangePickerWithPresets/DateRangePickerWithPresets';

interface Props {
  param: PortfolioReportParamDTO;
}

export const PortfolioReportParam: React.FC<Props> = ({ param }) => {
  const { mutateParamCache } = usePortfolioReportCache();

  if (param.type === ParamType.SingleDatePicker) {
    const id = `input--${useId(param.displayName)}`;
    return (
      <Wrapper mt="24">
        {param.displayName && (
          <Label htmlFor={id} mb="4" css="display: block">
            {param.displayName}
          </Label>
        )}

        <SingleDatePicker
          id={id}
          date={param.values ? stringToDate(param.values[0]) : new Date()}
          onChange={date => {
            mutateParamCache(param.id, { values: [dateToString(date)] });
          }}
          maxDate={new Date()}
          minDate={param.inceptionDate ? stringToDate(param.inceptionDate) : undefined}
        />
      </Wrapper>
    );
  }

  if (param.type === ParamType.DateRangePickerWithPresets) {
    return <DateRangePickerWithPresets param={param} mt="24" />;
  }

  // This is a special case (two parameters are connected - EntityType and Entity).
  // The dependency needs to be hardcoded like this for now.
  if (param.name === DependentParams.EntityType) {
    return <EntityTypeSelect param={param} mt="24" />;
  }

  if (param.type === ParamType.Dropdown && param.name !== DependentParams.Entity) {
    if (!param.options) {
      return null;
    }

    const value = param.values ? param.values[0] : null;

    return (
      <Wrapper mt="24">
        <Select
          label={param.displayName || undefined}
          id={param.displayName || undefined}
          value={value ? param.options.find(({ value: _value }) => value === _value) : null}
          options={param.options}
          onChange={option => {
            if (option) {
              mutateParamCache(param.id, { values: [option.value] });
            }
          }}
        />
      </Wrapper>
    );
  }

  return null;
};
